<script lang="ts" setup>
const appStateStore = useAppStateStore();
const currentMerchant = appStateStore.currentMerchant;
const props = defineProps({
  fontSize: {
    type: String,
    required: false,
    default: "text-lg",
  },
  id: {
    type: String,
    required: false,
    default: "",
  },
  specialText: {
    type: Boolean,
    required: false,
    default: false,
  },
  size: {
    type: String,
    validator: (value: string) => {
      return ["custom", "sm", "md", "lg", "xl", "2xl", "3xl"].includes(value);
    },
    default: undefined,
  },
  customClass: {
    type: String,
    required: false,
    default: undefined,
  },
  profilePicture: {
    type: String,
    required: false,
    default: "",
    validator: (value: string) => {
      return value === value.toUpperCase();
    },
  },
  specialBorder: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const { fontSize, id, profilePicture } = toRefs(props);
const { getUserProfile } = useUserController();
const userStore = useUserStore();

const extraClasses = computed(() => {
  switch (props.size) {
    case "custom":
      return props.customClass ? props.customClass : "";
    case "sm":
      return "w-6 h-6 ";
    case "md":
      return "w-12 h-12 ";
    case "lg":
      return "w-16 h-16 ";
    case "xl":
      return "p-2 w-20 h-20 ";
    case "2xl":
      return "p-1 w-24 h-24 ";
    case "3xl":
      return "p-1 w-32 h-32 ";
    default:
      return "w-40 h-40 p-1.5";
  }
});

const specialBorderColor = computed(() => {
  return props.specialBorder ? "border-[#FFB800]" : "border-secondary-500";
});

const tagLineFontSize = computed(() => {
  if (fontSize.value === "text-sm") {
    return "text-xs";
  } else if (fontSize.value === "text-md") {
    return "text-sm";
  } else if (fontSize.value === "text-base") {
    return "text-md";
  } else if (fontSize.value === "text-lg") {
    return "text-base";
  } else if (fontSize.value === "text-xl") {
    return "text-lg";
  } else if (fontSize.value === "text-2xl") {
    return "text-xl";
  } else {
    return fontSize.value;
  }
});

const specialTextColor = computed(() => {
  if (props.specialText) {
    return "text-[#FFB800]";
  } else {
    return "text-neutral-300";
  }
});

const routeToProfile = (id: string) => {
  if (!id) {
    return "";
  }

  const path =
    id === userStore.id ? `/collection/${currentMerchant}` : `/user/${id}`;
  return path;
};

const { data: user, status } = await useAsyncData(
  `userProfile/${id.value}`,
  async () => {
    if (id.value === userStore.id) {
      return {
        displayName: userStore.displayName,
        tagline: userStore.tagline,
        profilePicture: userStore.profilePicture,
        roles: userStore.roles,
      };
    }

    const { payload } = await getUserProfile(id.value);

    if (!payload) {
      return {
        displayName: "",
        tagline: "",
        profilePicture: "DEFAULT",
        roles: [],
      };
    }
    return {
      displayName: payload.displayName,
      tagline: payload.tagline,
      profilePicture: payload.profilePicture || "DEFAULT",
      roles: payload.roles || [],
    };
  },
  {
    default: () => ({
      displayName: "",
      tagline: "",
      profilePicture: "",
      roles: null,
    }),
    dedupe: "defer",
  }
);
</script>

<template>
  <NuxtLinkLocale :to="routeToProfile(id)" class="min-w-fit">
    <div class="group flex items-center gap-2">
      <img
        v-if="user.profilePicture"
        :src="getAvatar(user.profilePicture)"
        :alt="$t('main.imageAltTexts.CommonUserProfilePicture')"
        :class="`${extraClasses} ${
          profilePicture !== 'GFAL'
            ? `border-2 ${specialBorderColor}  rounded-full`
            : 'p-1'
        } transition-all transform duration-700 ease-in-out`" />
      <BaseIconAvatar v-else class="w-12" />
      <div class="truncate">
        <div v-if="user.displayName" class="flex items-baseline truncate">
          <p
            class="font-semibold text-center truncate user-card__name"
            :class="[fontSize, specialTextColor]">
            {{ user.displayName }}
          </p>
          <p
            v-if="user.tagline"
            class="font-semibold text-center truncate user-card__tagline"
            :class="[tagLineFontSize, specialTextColor]">
            {{ "#" + user.tagline }}
          </p>
        </div>
        <div
          v-else
          class="min-w-36 h-5 rounded-sm bg-gray-700 animate-pulse"></div>
      </div>
      <BaseIconInfluencerBadge
        v-if="user.roles && user.roles.includes('influencer')" />
    </div>
  </NuxtLinkLocale>
</template>
